goog.declareModuleId('yext.m4.confcode.adminconsole.Sidebar');

import {ResourceNode} from 'goog:yext.m4.confcode.adminconsole.shapes';
import FileTree from '/src/com/yext/sitesgitstorm/js/src/editor/tree/FileTree';
import {TreeNodeShape} from '/src/com/yext/sitesgitstorm/js/src/editor/tree/nodes/shapesJsDoc';

/**
 * Admin console sidebar component.
 *
 * @author Ethan Cohen (ecohen@yext.com)
 */

/**
 * @typedef {{
 *  resourceNodes: Array<ResourceNode>,
 *  rootNodeId: string,
 *  onAddOrUploadFiles: function(string),
 *  onExpandDirectory: function(string),
 *  onSelectFile: function(string, string, boolean),
 *  selectedNodeId: string,
 *  shouldOrganizeByResourceType: boolean,
 *  shouldShowFriendlyNames: boolean,
 *  shouldIncludeReadOnly: boolean,
 *  treeSelectMode: string,
 *  onDeleteFile: function(TreeNodeShape),
 *  onDeleteDirectory: function(string),
 *  onDoubleClickDirectory: function(string),
 *  onBulkSelect: function(string, boolean),
 * }}
 */
let SidebarProps;


/**
 * Renders the AdminConsole sidebar.
 *
 * This is a stateless component that mostly just converts resources into the shape expected
 * by the FileTree component and then renders that component.
 *
 * @param {SidebarProps} props
 * @returns {React.ReactNode}
 */
export function Sidebar(props) {
  const reducer =
    reduceResourceNodeToTreeNodeShape(
      props.shouldOrganizeByResourceType,
      props.shouldShowFriendlyNames,
      props.shouldIncludeReadOnly);
  const initial = {nodeIdToNode: new Map(), filteredOutResources: new Set()};
  const {nodeIdToNode, filteredOutResources} =
    props.resourceNodes.reduce(reducer, initial);

  // If we filtered out any resources, we want to delete any folder nodes that are now
  // empty.
  if (filteredOutResources.size > 0) {
    nodeIdToNode.forEach((node, id) => {
      if (node['type'] === 'tree' && node['children'].size === 0) {
        deleteNode(node, nodeIdToNode);
      }
    });
  }

  const expandedNodeIds = new Set(
    [...nodeIdToNode.values()]
      .filter(node => node['expanded'])
      .map(node => node['id']),
  );

  const onClickFile = node => {
    const id = node['id'];
    const oldSelectedFileId = props.selectedNodeId;
    props.onSelectFile(oldSelectedFileId, id, node['readOnly']);
  };

  return <FileTree
    rootNodeId={props.rootNodeId}
    nodeIdToNode={nodeIdToNode}
    selectedFileNodeIds={new Set(
      props.selectedNodeId ? [props.selectedNodeId] : [])}
    expandedNodeIds={expandedNodeIds}
    onDoubleClickDirectory={props.onDoubleClickDirectory}
    onExpandDirectory={props.onExpandDirectory}
    onClickFile={onClickFile}
    onAddOrUploadFiles={props.onAddOrUploadFiles}
    onDeleteFile={props.onDeleteFile}
    onDeleteDirectory={props.onDeleteDirectory}
    onBulkSelectDirectory={props.onBulkSelect}
    onBulkSelectFile={props.onBulkSelect}
    hasEditPermission={true}
    treeSelectMode={props.treeSelectMode}
    treeActionsMode={'button'}
  />;
}

/**
 * Deletes the specified node from the map, removes references to it from its parent. If the
 * parent then becomes empty, recursively deletes that node as well. This is NOT a pure function, it
 * modifies the nodeIdToNode map.
 *
 * @param {TreeNodeShape} node the node to delete
 * @param {Map<string, TreeNodeShape>} nodeIdToNode a map of node ids to nodes
 */
const deleteNode = (node, nodeIdToNode) => {
  const id = node['id'];
  nodeIdToNode.delete(id);
  const parent = nodeIdToNode.get(node['parentId']);
  parent['children'].delete(id);
  if (parent['type'] === 'tree' && parent['children'].size === 0) {
    deleteNode(parent, nodeIdToNode);
  }
};

/**
 * A partially applied reducer function for resource nodes.
 *
 * Uses the provided settings flags to build a reducer function that converts the ResourceNode shape
 * to the TreeNodeShape that is used for the FileTree component and accumulates the converted nodes
 * in a map by their path.
 *
 * Keeps track of the set of filtered out resources, so that we can delete references as needed
 * afterwards.
 *
 * @param {boolean} organizeByResourceType if the primary id should be the resource type
 * @param {boolean} showFriendlyNames if the display name should be the friendly name
 * @param {boolean} includeReadOnly if read only files should be included
 * @returns {function( {nodeIdToNode: Map<string, TreeNodeShape>, filteredOutResources: Set<string>}, ResourceNode)} the reducer
 */
const reduceResourceNodeToTreeNodeShape =
  (organizeByResourceType,
    showFriendlyNames,
    includeReadOnly) => ({nodeIdToNode, filteredOutResources}, node) => {
    const parentId =
      organizeByResourceType ? node['resourceTypeParentId'] : node['namespacedParentId'];

    const id =
     organizeByResourceType ? node['resourceTypeId'] : node['namespacedId'];

    // filter read only resources if specified
    if (!includeReadOnly && node['readOnly']) {
      if (nodeIdToNode.has(parentId)) {
        const parent = nodeIdToNode.get(parentId);
        parent['children'].delete(id);
      }

      filteredOutResources.add(id);
      return {nodeIdToNode, filteredOutResources};
    }

    const children =
      organizeByResourceType ? node['resourceTypeChildren'] : node['namespacedChildren'];
    filteredOutResources.forEach(r => children.delete(r));

    const converted = {
      id: organizeByResourceType
        ? node['resourceTypeId'] : node['namespacedId'],
      parentId: parentId,
      displayName: showFriendlyNames
        ? node['resourceDisplayNames']['friendly']
        : node['resourceDisplayNames']['default'],
      path: organizeByResourceType
        ? node['resourceTypePath']
        : node['namespacedPath'],
      type: node['type'],
      expanded: node['expanded'],
      isBulkSelected: node['isBulkSelected'],
      hasError: node['hasError'],
      children: children,
      readOnly: node['readOnly'],
    };

    nodeIdToNode.set(converted['id'], converted);
    return {nodeIdToNode, filteredOutResources};
  };
